/**
 * Drafting a sales document. The totals preview uses the same arithmetic as
 * the server's tax engine; the server recomputes everything on save, so the
 * preview can never make an invoice wrong — only the server's figures print.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, UserPlus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Decimal, amount, dec } from '../../lib/money';
import { manilaToday, useAccounts, useBranches, useContacts, useItems, useTaxCodes, type TaxCode } from '../../lib/queries';
import { useCompany } from '../setup/CompanyPage';
import { AccountSelect } from '../../components/AccountSelect';
import { Alert, Button, Card, Field, Money, PageHeader, Skeleton, filterClass, inputClass } from '../../components/ui';
import { ContactDialog } from './ContactsPage';
import { KIND_LABEL, type SalesDetail, type SalesKind, type SalesSummary } from './types';

interface Line {
  key: number;
  itemId: string;
  description: string;
  quantity: string;
  uom: string;
  unitPrice: string;
  discount: string;
  taxCode: string;
  accountId: string;
}

let nextKey = 1;

const r2 = (d: Decimal): Decimal => d.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

/** Mirror of the server's computeLine (tax-engine.ts). */
function preview(line: Line, code: TaxCode | undefined, inclusive: boolean, scPwd: boolean) {
  const amt = r2(dec(line.quantity).times(dec(line.unitPrice)).minus(dec(line.discount)));
  const vat = code && code.kind === 'VAT' && (code.treatment === 'VATABLE' || code.treatment === 'GOVERNMENT');
  if (!vat) return { net: amt, tax: new Decimal(0), gross: amt, treatment: code?.treatment ?? 'NOT_APPLICABLE' };
  const rate = dec(code.rate);
  let net: Decimal;
  let tax: Decimal;
  if (inclusive) {
    net = r2(amt.div(rate.plus(1)));
    tax = amt.minus(net);
  } else {
    net = amt;
    tax = r2(amt.times(rate));
  }
  if (scPwd && code.treatment === 'VATABLE') return { net, tax: new Decimal(0), gross: net, treatment: 'SC' };
  return { net, tax, gross: net.plus(tax), treatment: code.treatment };
}

export function SalesEditorPage(): JSX.Element {
  const { id, kind: kindParam } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const company = useCompany();
  const customers = useContacts('customer');
  const branches = useBranches();
  const items = useItems();
  const taxCodes = useTaxCodes();
  const accounts = useAccounts();
  const existing = useQuery({ queryKey: ['sales-doc', id], queryFn: () => api<SalesDetail>(`/sales-documents/${id}`), enabled: Boolean(id) });

  const kind = (existing.data?.kind ?? kindParam?.toUpperCase() ?? 'SALES_INVOICE') as SalesKind;
  const isMemo = kind === 'CREDIT_MEMO' || kind === 'DEBIT_MEMO';
  const defaultTax = company.data?.vatStatus === 'NON_VAT' ? 'PT' : 'VAT12';

  const [contactId, setContactId] = useState(search.get('contact') ?? '');
  const [branchId, setBranchId] = useState('');
  const [date, setDate] = useState(manilaToday());
  const [dueDate, setDueDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [inclusive, setInclusive] = useState<boolean | null>(null);
  const [scPwdType, setScPwdType] = useState<'' | 'SENIOR_CITIZEN' | 'PWD'>('');
  const [scPwdIdNumber, setScPwdIdNumber] = useState('');
  const [scPwdName, setScPwdName] = useState('');
  const [invoiceId, setInvoiceId] = useState(search.get('invoice') ?? '');
  const [lines, setLines] = useState<Line[]>([]);
  const [showAccounts, setShowAccounts] = useState(false);
  const [newCustomer, setNewCustomer] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const blank = (): Line => ({ key: nextKey++, itemId: '', description: '', quantity: '1', uom: '', unitPrice: '', discount: '', taxCode: defaultTax, accountId: '' });

  useEffect(() => {
    if (!lines.length && company.data && !id) setLines([blank()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.data]);
  useEffect(() => {
    if (!branchId && branches.data?.length) setBranchId(branches.data.find((b) => b.isHeadOffice)?.id ?? branches.data[0].id);
  }, [branches.data, branchId]);
  useEffect(() => {
    const d = existing.data;
    if (!d) return;
    setContactId(d.contactId);
    setBranchId(d.branchId);
    setDate(d.date);
    setDueDate(d.dueDate ?? '');
    setReference(d.reference);
    setNotes(d.notes);
    setInclusive(d.pricesIncludeVat);
    setScPwdType(d.scPwdType ?? '');
    setScPwdIdNumber(d.scPwdIdNumber);
    setScPwdName(d.scPwdName);
    setInvoiceId(d.invoice?.id ?? '');
    setLines(d.lines.map((l) => ({ key: nextKey++, itemId: l.itemId ?? '', description: l.description, quantity: String(Number(l.quantity)), uom: l.uom, unitPrice: dec(l.unitPrice).toFixed(2), discount: dec(l.discount).isZero() ? '' : dec(l.discount).toFixed(2), taxCode: l.taxCode, accountId: l.accountId })));
  }, [existing.data]);

  const invoices = useQuery({
    queryKey: ['sales', 'memo-targets', contactId],
    queryFn: () => api<{ data: SalesSummary[] }>(`/sales-documents?kind=SALES_INVOICE&status=ISSUED&contactId=${contactId}&pageSize=200`).then((r) => r.data),
    enabled: isMemo && Boolean(contactId),
  });

  const pricesInclusive = inclusive ?? company.data?.pricesIncludeVat ?? false;
  const codeOf = (c: string) => taxCodes.data?.find((t) => t.code === c);
  const totals = useMemo(() => {
    const rows = lines.map((l) => preview(l, codeOf(l.taxCode), pricesInclusive, Boolean(scPwdType)));
    const sum = (f: (r: (typeof rows)[number]) => Decimal) => rows.reduce((a, r) => a.plus(f(r)), new Decimal(0));
    const scBase = rows.filter((r) => r.treatment === 'SC').reduce((a, r) => a.plus(r.net), new Decimal(0));
    const scDiscount = scPwdType ? r2(scBase.times('0.2')) : new Decimal(0);
    return {
      rows,
      vatable: sum((r) => (r.treatment === 'VATABLE' ? r.net : new Decimal(0))),
      vat: sum((r) => r.tax),
      scDiscount,
      total: sum((r) => r.gross).minus(scDiscount),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, taxCodes.data, pricesInclusive, scPwdType]);

  const update = (key: number, patch: Partial<Line>): void => setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const pickItem = (key: number, itemId: string): void => {
    const item = items.data?.find((i) => i.id === itemId);
    update(key, item ? { itemId, description: item.name, unitPrice: dec(item.salesPrice).toFixed(2), uom: item.uom, taxCode: item.salesTaxCode ?? defaultTax, accountId: item.salesAccountId ?? '' } : { itemId: '' });
  };
  const pickCustomer = (cid: string): void => {
    setContactId(cid);
    setInvoiceId('');
    const c = customers.data?.find((x) => x.id === cid);
    if (c?.defaultTaxCode) setLines((all) => all.map((l) => (l.itemId ? l : { ...l, taxCode: c.defaultTaxCode! })));
  };

  const save = useMutation({
    mutationFn: async (then: 'draft' | 'issue') => {
      const body = {
        kind,
        branchId,
        contactId,
        date,
        dueDate: dueDate || null,
        reference,
        notes,
        pricesIncludeVat: pricesInclusive,
        invoiceId: isMemo ? invoiceId || null : null,
        scPwdType: scPwdType || null,
        scPwdIdNumber,
        scPwdName,
        rowVersion: existing.data?.rowVersion,
        lines: lines
          .filter((l) => l.description.trim() || l.unitPrice)
          .map((l) => ({ itemId: l.itemId || null, description: l.description, quantity: l.quantity || '0', uom: l.uom, unitPrice: l.unitPrice || '0', discount: l.discount || undefined, taxCode: l.taxCode, accountId: l.accountId || null })),
      };
      let doc = id ? await api<SalesDetail>(`/sales-documents/${id}`, { method: 'PATCH', body }) : await api<SalesDetail>('/sales-documents', { method: 'POST', body });
      let issueError: string | undefined;
      if (then === 'issue') {
        // The draft is saved either way; an issue problem is shown on its page.
        try {
          doc = await api<SalesDetail>(`/sales-documents/${doc.id}/issue`, { method: 'POST', body: {} });
        } catch (caught) {
          issueError = caught instanceof ApiError ? caught.message : 'Could not issue.';
        }
      }
      return { doc, issueError };
    },
    onSuccess: ({ doc, issueError }) => {
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-doc', doc.id] });
      navigate(`/sales/${doc.id}`, { replace: true, state: { warnings: doc.warnings, issueError } });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'Could not save.')),
  });

  if ((id && existing.isLoading) || company.isLoading || !lines.length) return <Skeleton className="h-96 w-full" />;
  if (existing.data && existing.data.status !== 'DRAFT') return <Alert tone="warning">Only drafts can be edited.</Alert>;

  const issueLabel = kind === 'QUOTATION' || kind === 'SALES_ORDER' ? 'Save and send' : 'Save and issue';
  const vatCompany = company.data?.vatStatus === 'VAT';

  return (
    <>
      <PageHeader title={id ? `Edit ${KIND_LABEL[kind].toLowerCase()}` : `New ${KIND_LABEL[kind].toLowerCase()}`} description={kind === 'SALES_INVOICE' ? 'The serial number is taken from your registered series when you issue. Issued invoices cannot be edited.' : undefined} />
      {error && (
        <div className="mb-4">
          <Alert tone="critical">
            {error.message}
            {error.fieldMessages.length > 1 && <ul className="mt-1 list-disc pl-5">{error.fieldMessages.slice(1, 6).map((m) => <li key={m}>{m}</li>)}</ul>}
          </Alert>
        </div>
      )}
      <Card className="mb-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="md:col-span-2">
            <Field label="Customer" required error={error?.fields.contactId}>
              <div className="flex gap-2">
                <select className={inputClass} value={contactId} onChange={(e) => pickCustomer(e.target.value)}>
                  <option value="">Choose a customer</option>
                  {(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.registeredName}{c.tinFormatted ? ` · ${c.tinFormatted}` : ''}</option>)}
                </select>
                <Button type="button" variant="secondary" aria-label="New customer" onClick={() => setNewCustomer(true)}><UserPlus aria-hidden="true" className="h-4 w-4" /></Button>
              </div>
            </Field>
          </div>
          <Field label="Branch" required>
            <select className={inputClass} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {(branches.data ?? []).filter((b) => b.active).map((b) => <option key={b.id} value={b.id}>{b.code} {b.name}</option>)}
            </select>
          </Field>
          <Field label="Date" required error={error?.fields.date}>
            <input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          {(kind === 'SALES_INVOICE' || kind === 'DEBIT_MEMO') && (
            <Field label="Due date" hint="Defaults to the customer's terms.">
              <input type="date" className={inputClass} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          )}
          {isMemo && (
            <div className="md:col-span-2">
              <Field label="Invoice being adjusted" required error={error?.fields.invoiceId}>
                <select className={inputClass} value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} disabled={!contactId}>
                  <option value="">{contactId ? 'Choose an invoice' : 'Choose the customer first'}</option>
                  {(invoices.data ?? []).map((i) => <option key={i.id} value={i.id}>{i.serial} · {i.date} · balance {amount(i.balanceDue)}</option>)}
                </select>
              </Field>
            </div>
          )}
          <Field label="Reference / PO no.">
            <input className={inputClass} value={reference} maxLength={100} onChange={(e) => setReference(e.target.value)} />
          </Field>
          {vatCompany && (
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={pricesInclusive} onChange={(e) => setInclusive(e.target.checked)} /> Prices include VAT
            </label>
          )}
        </div>
        {kind === 'SALES_INVOICE' && (
          <div className="mt-4 grid gap-4 border-t border-slate-100 pt-4 md:grid-cols-3 dark:border-slate-800">
            <Field label="Senior citizen / PWD sale" hint="VAT-exempt, less 20% (RA 9994 / RA 10754).">
              <select className={inputClass} value={scPwdType} onChange={(e) => setScPwdType(e.target.value as typeof scPwdType)}>
                <option value="">No</option>
                <option value="SENIOR_CITIZEN">Senior citizen</option>
                <option value="PWD">Person with disability</option>
              </select>
            </Field>
            {scPwdType && (
              <>
                <Field label={scPwdType === 'PWD' ? 'PWD ID no.' : 'OSCA / SC ID no.'} required error={error?.fields.scPwdIdNumber}>
                  <input className={inputClass} value={scPwdIdNumber} onChange={(e) => setScPwdIdNumber(e.target.value)} />
                </Field>
                <Field label="Name on the ID" required>
                  <input className={inputClass} value={scPwdName} onChange={(e) => setScPwdName(e.target.value)} />
                </Field>
              </>
            )}
          </div>
        )}
      </Card>

      <Card title="Lines" actions={<label className="flex items-center gap-2 text-xs text-slate-500"><input type="checkbox" className="h-4 w-4" checked={showAccounts} onChange={(e) => setShowAccounts(e.target.checked)} /> Choose revenue accounts</label>}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="w-44 pb-2 pr-2">Item</th>
                <th className="pb-2 pr-2">Description</th>
                <th className="w-20 pb-2 pr-2 text-right">Qty</th>
                <th className="w-28 pb-2 pr-2 text-right">Unit price</th>
                <th className="w-24 pb-2 pr-2 text-right">Discount</th>
                <th className="w-36 pb-2 pr-2">Tax</th>
                {showAccounts && <th className="w-48 pb-2 pr-2">Account</th>}
                <th className="w-28 pb-2 pr-2 text-right">Amount</th>
                <th className="w-10 pb-2" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.key} className="align-top">
                  <td className="py-1.5 pr-2">
                    <select className={`${filterClass} w-full`} value={l.itemId} onChange={(e) => pickItem(l.key, e.target.value)} aria-label={`Line ${i + 1} item`}>
                      <option value="">—</option>
                      {(items.data ?? []).filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{x.code} {x.name}</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 pr-2"><input className={`${filterClass} w-full`} value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} aria-label={`Line ${i + 1} description`} /></td>
                  <td className="py-1.5 pr-2"><input className={`${filterClass} w-full text-right tabular-nums`} inputMode="decimal" value={l.quantity} onChange={(e) => update(l.key, { quantity: e.target.value })} aria-label={`Line ${i + 1} quantity`} /></td>
                  <td className="py-1.5 pr-2"><input className={`${filterClass} w-full text-right tabular-nums`} inputMode="decimal" value={l.unitPrice} onChange={(e) => update(l.key, { unitPrice: e.target.value.replace(/[^\d.]/g, '') })} aria-label={`Line ${i + 1} unit price`} /></td>
                  <td className="py-1.5 pr-2"><input className={`${filterClass} w-full text-right tabular-nums`} inputMode="decimal" value={l.discount} onChange={(e) => update(l.key, { discount: e.target.value.replace(/[^\d.]/g, '') })} aria-label={`Line ${i + 1} discount`} /></td>
                  <td className="py-1.5 pr-2">
                    <select className={`${filterClass} w-full`} value={l.taxCode} onChange={(e) => update(l.key, { taxCode: e.target.value })} aria-label={`Line ${i + 1} tax`}>
                      {(taxCodes.data ?? []).filter((t) => (vatCompany ? t.kind === 'VAT' : t.kind !== 'VAT' || t.treatment === 'EXEMPT')).map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
                    </select>
                  </td>
                  {showAccounts && (
                    <td className="py-1.5 pr-2">
                      <AccountSelect accounts={(accounts.data ?? []).filter((a) => a.type === 'INCOME')} value={l.accountId} onChange={(accountId) => update(l.key, { accountId })} placeholder="Default" className={`${filterClass} w-full`} />
                    </td>
                  )}
                  <td className="py-1.5 pr-2 pt-3 text-right"><Money>{amount(totals.rows[i]?.gross.toString())}</Money></td>
                  <td className="py-1.5">
                    <button type="button" className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 dark:hover:bg-red-950" aria-label={`Remove line ${i + 1}`} disabled={lines.length <= 1} onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))}>
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <Button type="button" size="sm" variant="secondary" onClick={() => setLines((all) => [...all, blank()])}><Plus aria-hidden="true" className="h-4 w-4" /> Add line</Button>
          <dl className="min-w-[16rem] space-y-1 text-sm">
            {vatCompany && <div className="flex justify-between gap-6"><dt className="text-slate-500">VATable sales</dt><dd><Money>{amount(totals.vatable.toString())}</Money></dd></div>}
            {vatCompany && <div className="flex justify-between gap-6"><dt className="text-slate-500">VAT</dt><dd><Money>{amount(totals.vat.toString())}</Money></dd></div>}
            {!totals.scDiscount.isZero() && <div className="flex justify-between gap-6"><dt className="text-slate-500">SC/PWD discount</dt><dd><Money>-{amount(totals.scDiscount.toString())}</Money></dd></div>}
            <div className="flex justify-between gap-6 border-t border-slate-200 pt-1 text-base font-bold dark:border-slate-700"><dt>Total</dt><dd><Money>{amount(totals.total.toString())}</Money></dd></div>
          </dl>
        </div>
        <div className="mt-4">
          <Field label="Notes (printed)">
            <textarea className={inputClass} rows={2} value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
      </Card>

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="secondary" busy={save.isPending && save.variables === 'draft'} disabled={!contactId} onClick={() => { setError(null); save.mutate('draft'); }}>Save draft</Button>
        <Button busy={save.isPending && save.variables === 'issue'} disabled={!contactId} onClick={() => { setError(null); save.mutate('issue'); }}>{issueLabel}</Button>
      </div>
      {newCustomer && <ContactDialog role="customer" contact={null} onClose={() => setNewCustomer(false)} onSaved={(c) => setContactId(c.id)} />}
    </>
  );
}
